import React, { useState } from 'react';
import Icons from '../components/Icons';
import CarCard from '../components/CarCard';
import { CTASection } from '../components/Sections';
import FooterSection from '../components/Footer';

const LuxuryCarsPage = ({ cars, isDark, openBookingModal }) => {
  const [filters, setFilters] = useState({ transmission: '', fuel_type: '', seats: '' });
  
  const filteredCars = cars.filter(car => {
    if (filters.transmission && car.transmission !== filters.transmission) return false;
    if (filters.fuel_type && car.fuel_type !== filters.fuel_type) return false;
    if (filters.seats && car.seating_capacity < parseInt(filters.seats)) return false;
    return true;
  });

  // Udaipur places to visit
  const udaipurPlaces = [
    {
      name: 'Lake Pichola',
      image: 'https://thumbs.dreamstime.com/b/lake-pichola-udaipur-panoramic-view-city-lakes-udaipur-lake-pichola-ambrai-ghat-rajasthan-india-203501206.jpg',
      description: 'Experience the serene beauty of Lake Pichola with a scenic boat ride'
    },
    {
      name: 'City Palace',
      image: 'https://architectureprizecom.s3-us-west-2.amazonaws.com/uploads/96497/22-10657-21/full/6e3affd23ad7fc4cf4c8802c70570d5f.jpg',
      description: 'Explore the magnificent City Palace, a symbol of Udaipur\'s royal heritage'
    },
    {
      name: 'Jag Mandir',
      image: 'https://www.rajasthantourplanner.com/blog/wp-content/uploads/2021/11/Jag-Mandir-768x288.jpg',
      description: 'Visit the stunning island palace in the middle of Lake Pichola'
    },
    {
      name: 'Saheliyon Ki Bari',
      image: 'https://www.lasociedadgeografica.com/blog/uploads/2020/08/saheliyon-ki-bari-udaipur-tourism.jpg',
      description: 'Beautiful gardens with fountains and marble pavilions'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className={`relative min-h-[50vh] md:min-h-[60vh] flex flex-col justify-center overflow-hidden transition-colors duration-300`}>
        {/* Background */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=1920)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent"></div>
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 text-white">Luxury <span className="text-[#c9a962]">Cars</span></h1>
          <p className="text-lg sm:text-xl text-gray-400">Premium vehicles for unforgettable experiences in Udaipur</p>
        </div>
      </section>

      {/* Cars Listing Section */}
      <section className={`py-16 transition-colors duration-300 ${isDark ? 'bg-[#0d0d0d]' : 'bg-gray-100'}`}>
        <div className="container-custom px-4">
          <div className="flex flex-wrap gap-4 justify-center mb-12">
            <select className={`px-4 py-3 rounded-lg transition-all duration-300 ${isDark ? 'bg-[#1a1a1a] text-white border border-gray-700' : 'bg-white text-gray-900 border border-gray-200'}`} value={filters.transmission} onChange={(e) => setFilters({...filters, transmission: e.target.value})}>
              <option value="">All Transmission</option>
              <option value="Automatic">Automatic</option>
              <option value="Manual">Manual</option>
            </select>
            <select className={`px-4 py-3 rounded-lg transition-all duration-300 ${isDark ? 'bg-[#1a1a1a] text-white border border-gray-700' : 'bg-white text-gray-900 border border-gray-200'}`} value={filters.fuel_type} onChange={(e) => setFilters({...filters, fuel_type: e.target.value})}>
              <option value="">All Fuel Types</option>
              <option value="Petrol">Petrol</option>
              <option value="Diesel">Diesel</option>
            </select>
            <select className={`px-4 py-3 rounded-lg transition-all duration-300 ${isDark ? 'bg-[#1a1a1a] text-white border border-gray-700' : 'bg-white text-gray-900 border border-gray-200'}`} value={filters.seats} onChange={(e) => setFilters({...filters, seats: e.target.value})}>
              <option value="">All Seats</option>
              <option value="4">4+ Seats</option>
              <option value="5">5+ Seats</option>
              <option value="7">7+ Seats</option>
            </select>
          </div>
          {filteredCars.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCars.map((car) => <CarCard key={car.id} car={car} isDark={isDark} openBookingModal={openBookingModal} />)}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className={`text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>No cars match your filters.</p>
              <button onClick={() => setFilters({ transmission: '', fuel_type: '', seats: '' })} className="mt-4 px-6 py-2 bg-[#c9a962] text-black font-semibold rounded-lg hover:bg-[#b89952] transition-colors">
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Book Your Luxury Ride Section */}
      <section className={`py-16 md:py-24 transition-colors duration-300 ${isDark ? 'bg-[#1a1a1a]' : 'bg-white'}`}>
        <div className="container-custom px-4">
          <div className="text-center mb-12">
            <span className="text-[#c9a962] text-sm font-semibold uppercase tracking-[0.3em]">Contact Us</span>
            <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold mt-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              📞 Book Your <span className="text-[#c9a962]">Luxury Ride</span> Today
            </h2>
            <p className={`mt-4 max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Ready to explore Udaipur in style? Contact us now for instant booking and best deals!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
            {/* Location Card */}
            <div className={`group p-6 md:p-8 rounded-2xl text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#c9a962]/20 ${isDark ? 'bg-[#0d0d0d] hover:bg-[#222]' : 'bg-gray-50 hover:bg-white shadow-lg'}`}>
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#c9a962] to-[#a08040] rounded-full flex items-center justify-center text-3xl transform group-hover:scale-110 transition-transform duration-300">
                📍
              </div>
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Location</h3>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Udaipur, Rajasthan</p>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>City of Lakes</p>
            </div>

            {/* Phone/WhatsApp Card */}
            <div className={`group p-6 md:p-8 rounded-2xl text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#c9a962]/20 ${isDark ? 'bg-[#0d0d0d] hover:bg-[#222]' : 'bg-gray-50 hover:bg-white shadow-lg'}`}>
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#c9a962] to-[#a08040] rounded-full flex items-center justify-center text-3xl transform group-hover:scale-110 transition-transform duration-300">
                📲
              </div>
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Call / WhatsApp</h3>
              <a href="tel:9602360369" className="text-[#c9a962] text-lg font-semibold hover:underline">96023 60369</a>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Available 24×7</p>
            </div>

            {/* Timing Card */}
            <div className={`group p-6 md:p-8 rounded-2xl text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#c9a962]/20 ${isDark ? 'bg-[#0d0d0d] hover:bg-[#222]' : 'bg-gray-50 hover:bg-white shadow-lg'}`}>
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#c9a962] to-[#a08040] rounded-full flex items-center justify-center text-3xl transform group-hover:scale-110 transition-transform duration-300">
                🕒
              </div>
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Availability</h3>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>24 Hours / 7 Days</p>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Always at your service</p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <a 
              href="tel:9602360369" 
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black font-semibold uppercase tracking-wider rounded-lg hover:shadow-lg hover:shadow-[#c9a962]/30 transition-all duration-300 hover:scale-105 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call Now
            </a>
            <a 
              href="https://wa.me/919602360369?text=Hi! I'm interested in booking a car from Brothers Car Rental." 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-500 text-white font-semibold uppercase tracking-wider rounded-lg hover:bg-green-600 hover:shadow-lg transition-all duration-300 hover:scale-105 text-sm"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Explore Udaipur Section */}
      <section className={`py-16 md:py-24 transition-colors duration-300 ${isDark ? 'bg-[#0d0d0d]' : 'bg-gray-50'}`}>
        <div className="container-custom px-4">
          <div className="text-center mb-12">
            <span className="text-[#c9a962] text-sm font-semibold uppercase tracking-[0.3em]">Discover</span>
            <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold mt-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Explore <span className="text-[#c9a962]">Udaipur</span> with Us
            </h2>
            <p className={`mt-4 max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Visit these amazing places in the City of Lakes with our premium rental cars
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {udaipurPlaces.map((place, index) => (
              <div 
                key={index}
                className={`group relative overflow-hidden rounded-2xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#c9a962]/20 ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}
              >
                <div className="relative h-48 sm:h-56 overflow-hidden">
                  <img 
                    src={place.image} 
                    alt={place.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-lg font-bold text-white">{place.name}</h3>
                    <p className="text-sm text-gray-300 mt-1 line-clamp-2">{place.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FooterSection isDark={isDark} />
    </>
  );
};

export default LuxuryCarsPage;
